// frontend/src/stores/gameStore.js
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { GAME_STATES } from '../utils/constants';

const initialState = {
	// Connection & Identity
	socketId: null, // Store the client's own socket ID when connected
	error: null,
	isLoading: false,

	// Game Info
	gameId: localStorage.getItem('triviaGameId') || null, // Load last active game on start
	host: { id: null, name: null },
	settings: null,
	gameState: GAME_STATES.IDLE,

	// Player Info
	playerName: localStorage.getItem('playerName') || '', // Persist player name locally
	playerId: localStorage.getItem('triviaPlayerId') || null, // Load persistent ID on start
	players: [], // { id, name, score }

	// Gameplay State
	currentQuestion: null, // { text, type, category, options, timeLimit }
	questionNumber: 0,
	totalQuestions: 0,
	timeLeft: 0, // Visual timer countdown
	countdown: null, // General purpose countdown (game start, next question, results)
	hasAnswered: false, // Has client submitted answer for current question?
	lastAnswerResult: null, // { points, isCorrect } from server for feedback

	// Results
	roundResults: null, // { question: { text, correctAnswer }, playerResults: [...] }
	gameResults: null, // { players: [...], podium: [...] }

	// UI/Navigation Control
	navigationTarget: '/', // Path to navigate to, handled by UI effect
};

export const useGameStore = create(
	devtools(
		(set, get) => ({
			// --- State ---
			...initialState,

			// --- Actions ---

			// Basic Setters
			setSocketId: (id) => set({ socketId: id }),
			setError: (message) => set({ error: message, isLoading: false }),
			clearError: () => set({ error: null }),
			setLoading: (loading) => set({ isLoading: loading }),
			setPlayerId: (id) => set({ playerId: id }),
			setPlayerName: (name) => {
				const sanitizedName = name.slice(0, 20);
				localStorage.setItem('playerName', sanitizedName); // Persist
				set({ playerName: sanitizedName });
			},
			setTimeLeft: (time) => set({ timeLeft: Math.max(0, time) }),
			setCountdown: (count) => set({ countdown: count }),
			setNavigationTarget: (path) => set({ navigationTarget: path }),
			clearNavigationTarget: () => set({ navigationTarget: null }),

			// --- Handlers for Incoming Socket Events ---

			// Note: These align with server-side emit payloads now
			handleGameCreated: ({ playerId, gameId, host, settings, players, isHost, totalQuestions }) => {
				localStorage.setItem('triviaPlayerId', playerId);
				localStorage.setItem('triviaGameId', gameId);
				set({
					gameId,
					playerId,
					host,
					settings,
					players,
					isHost, // Server confirms if creator is host
					totalQuestions,
					gameState: GAME_STATES.LOBBY,
					error: null,
					isLoading: false,
					navigationTarget: `/lobby/${gameId}`, // Trigger navigation via state
				});
			},
			handleQuestionsLoaded: ({ totalQuestions }) => {
				set({
					totalQuestions,
				});
			},

			handleJoinedGame: ({ playerId, gameId, host, settings, players, isHost, totalQuestions }) => {
				localStorage.setItem('triviaPlayerId', playerId);
				localStorage.setItem('triviaGameId', gameId);
				set({
					gameId,
					playerId,
					host,
					settings,
					totalQuestions,
					players,
					isHost, // Server confirms joiner is not host
					gameState: GAME_STATES.LOBBY,
					error: null,
					isLoading: false,
					navigationTarget: `/lobby/${gameId}`, // Trigger navigation via state
				});
			},

			handlePlayerUpdate: ({ players }) => {
				set({ players });
			},

			handleHostChanged: ({ host }) => {
				const currentPlayerId = get().playerId;
				set({
					host,
					isHost: host.id === currentPlayerId, // Check if *this* client is the new host
				});
			},

			handleGameStartInitiated: ({ countdown }) => {
				const gameId = get().gameId;
				set({
					gameState: GAME_STATES.COUNTDOWN_START,
					countdown,
					error: null,
					// Navigate only if gameId exists
					navigationTarget: gameId ? `/game/${gameId}` : null,
				});
				if (!gameId) console.warn('Game start initiated, but no gameId found in store for navigation.');
			},

			handleQuestionCountdown: ({ countdown }) => {
				// Used between questions
				set({ gameState: GAME_STATES.COUNTDOWN_NEXT, countdown });
			},

			handleResultsCountdown: ({ countdown }) => {
				set({ gameState: GAME_STATES.RESULTS_COUNTDOWN, countdown });
			},

			handleNewQuestion: ({ question, questionNumber, totalQuestions }) => {
				set({
					gameState: GAME_STATES.PLAYING,
					currentQuestion: question,
					questionNumber,
					totalQuestions,
					timeLeft: question.timeLimit, // Start visual timer with server limit
					hasAnswered: false, // Reset answered flag
					lastAnswerResult: null, // Clear previous answer feedback
					roundResults: null, // Clear previous round results
					countdown: null, // Clear any active countdown
					error: null,
				});
			},

			handleAnswerResult: ({ points, isCorrect }) => {
				// Feedback for the player who submitted

				set({
					hasAnswered: true, // Mark as answered for UI purposes
					lastAnswerResult: { points, isCorrect },
				});
			},

			handleRoundResults: (results) => {
				// Display results, game state changes
				set({
					gameState: GAME_STATES.ROUND_END,
					roundResults: results,
					currentQuestion: null, // Clear current question display
					timeLeft: 0,
					countdown: null,
				});
			},

			handleGameResults: (results) => {
				const gameId = get().gameId;
				set({
					gameState: GAME_STATES.ENDED,
					gameResults: results,
					currentQuestion: null,
					timeLeft: 0,
					countdown: null,
					// Navigate only if gameId exists
					navigationTarget: gameId ? `/results/${gameId}` : null,
				});
				if (!gameId) console.warn('Game results received, but no gameId found in store for navigation.');

				// // Clean localStorage
				localStorage.removeItem('triviaGameId');
			},

			// The master handler for state synchronization
			handleGameStateSync: (payload) => {
				console.log('Syncing to authoritative server state:', payload.gameState);

				const {
					gameState,
					gameId,
					host,
					players,
					settings,
					totalQuestions,
					currentQuestion,
					currentQuestionIndex,
					roundResults,
					gameResults,
					timeRemaining,
				} = payload;

				if (gameState === GAME_STATES.ENDED) {
					return get().handleGameResults(gameResults);
				}

				// Update the entire game state at once
				set({
					gameId,
					gameState,
					host,
					players,
					settings,
					totalQuestions,
					currentQuestion,
					questionNumber: currentQuestionIndex + 1,
					roundResults,
					gameResults,
					error: null,
					isLoading: false,
					// Sync the timer!
					timeLeft: timeRemaining ? Math.floor(timeRemaining / 1000) : settings.timePerQuestion || 0,
					// Determine navigation based on the synced state
					navigationTarget: get().gameId !== gameId ? `/game/${gameId}` : null,
				});
			},

			handleGameEndedExternally: ({ reason }) => {
				// Game ended by server (e.g., host disconnect, empty)
				const preservedPlayerName = get().playerName; // Keep player name
				set({
					...initialState, // Reset most state
					playerName: preservedPlayerName, // Restore name
					error: reason || 'The game has ended.', // Show reason
					navigationTarget: '/', // Navigate home
				});
			},

			handleError: ({ message }) => {
				// Generic server error
				set({ error: message || 'An unknown error occurred.' });
			},

			// --- Local Actions ---

			// Reset game state, potentially keeping player name
			resetGame: () => {
				// // Clean localStorage
				localStorage.removeItem('triviaGameId');

				const preservedPlayerName = get().playerName;
				const preservedSocketId = get().socketId; // Keep socket ID if connection persists
				set({
					...initialState,
					playerName: preservedPlayerName,
					socketId: preservedSocketId,
				});
			},
		}),
		{
			name: 'GameStore',
			// Add state persistence if needed (e.g., persist playerName, settings)
			// using persist middleware
		}
	)
);

// Example Selector (Optional)
export const selectIsGameActive = (state) =>
	state.gameState !== GAME_STATES.IDLE && state.gameState !== GAME_STATES.ENDED;
